// src/components/coach/CoachSupportCard.jsx
//
// Inline support bubble in the AI Coach conversation. Appears when the
// member's answers carry a safety flag (once, at the start) or when a message
// contains crisis language (each time). Real people first, model second.

import React from 'react';
import { HeartHandshake } from 'lucide-react';
import SupportOutlet from '../dashboard/SupportOutlet';
import CrisisResources from '../dashboard/CrisisResources';
import { resolveAgeTier } from '../../utils/ageTier';
import { themeLabels } from '../../data/crisisResources';

const COPY = {
    flag: {
        title: 'A few of your recent answers suggest things are heavy right now.',
        body: "I'm here to talk — and a person can help more than I can. These are free and confidential."
    },
    message: {
        title: "That sounds really heavy. You don't have to carry it on your own.",
        body: 'Please reach out to a person right now — these lines are free, confidential and open.'
    }
};

// A theme card names what it heard, so the member knows why these lines and
// not the generic ones: "about bullying" reads as listening; a bare list of
// phone numbers reads as being handed off.
const themeCopy = (themes) => {
    const labels = themeLabels(themes);
    const what = labels.length ? labels.join(' and ') : 'this';
    return {
        title: `It sounds like this might be about ${what}.`,
        body: 'These are people who help with exactly that. Free, confidential, and used to hearing it.'
    };
};

const CoachSupportCard = ({ currentUser, reason = 'flag', topicId = null, themes = null }) => {
    const copy = reason === 'theme' ? themeCopy(themes) : (COPY[reason] || COPY.flag);
    return (
        <div className="flex justify-start">
            <div className="w-full max-w-[95%] bg-rose-50 border border-rose-200 rounded-2xl rounded-tl-none p-4 shadow-sm animate-in fade-in duration-300">
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"><HeartHandshake size={16} /></div>
                    <div className="min-w-0">
                        <p className="text-sm font-black text-slate-900 leading-snug">{copy.title}</p>
                        <p className="text-xs text-slate-600 font-medium mt-1">{copy.body}</p>
                    </div>
                </div>
                <div className="mt-3">
                    <SupportOutlet
                        currentUser={currentUser}
                        topicId={topicId}
                        className="w-full px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors"
                    />
                </div>
                <div className="mt-3">
                    <CrisisResources memberType={currentUser?.memberType} ageTier={resolveAgeTier(currentUser)} topicId={topicId} themes={reason === 'theme' ? themes : null} heading="Right now" compact />
                </div>
            </div>
        </div>
    );
};

export default CoachSupportCard;
